import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

/**
 * Button — variantes institucionais (GOVIO / Lei 14.133/2021).
 *
 * - `institutional` (default) → CTA primário azul institucional (#1A4B7C),
 *   borda inferior 1px sombra para sensação "press-able", touch-target ≥40px.
 * - `secondary` → outline papel/tinta (Anexar, Cancelar leve).
 * - `destructive` → ações irreversíveis (Cancelar processo).
 * - `ghost` / `link` → tertiário (Voltar, Ver mais).
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap rounded-md",
    "text-sm font-medium",
    "ring-offset-background transition-colors duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        institutional: [
          "bg-[var(--color-ratia-blue-600)] text-white",
          "shadow-[0_1px_0_rgba(0,0,0,0.08)]",
          "hover:bg-[var(--color-ratia-blue-700)]",
          "active:bg-[var(--color-ratia-blue-700)]",
        ].join(" "),
        default: [
          "bg-[var(--color-ratia-blue-600)] text-white",
          "shadow-[0_1px_0_rgba(0,0,0,0.08)]",
          "hover:bg-[var(--color-ratia-blue-700)]",
          "active:bg-[var(--color-ratia-blue-700)]",
        ].join(" "),
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: [
          "border border-[var(--color-ratia-line)] bg-card",
          "text-[var(--color-ratia-ink)]",
          "hover:bg-[var(--color-ratia-blue-50)] hover:text-[var(--color-ratia-blue-700)]",
        ].join(" "),
        secondary: [
          "border border-[var(--color-ratia-line)] bg-card",
          "text-[var(--color-ratia-ink)]",
          "hover:bg-[var(--color-ratia-blue-50)] hover:text-[var(--color-ratia-blue-700)]",
        ].join(" "),
        ghost:
          "text-[var(--color-ratia-ink-2)] hover:bg-[var(--color-ratia-blue-50)] hover:text-[var(--color-ratia-blue-700)]",
        link: "text-[var(--color-ratia-blue-600)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-5 py-2.5",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
